import { DOCUMENT } from '@angular/common';
import { APP_ID, DestroyRef, Inject, NgModule, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BrowserModule, Title } from '@angular/platform-browser';
import { SwRegistrationOptions } from '@angular/service-worker';
import { BrandingService } from '@next/core-lib/branding';
import { provideAgrBrowser } from '@next/core-lib/browser';

import { EnvironmentInjector } from '@next/core-lib/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { LocaleService } from '@next/core-lib/i18n';
import { PWA_INSTALL_PROMPT_MODE } from '@next/core-lib/pwa';

import { environment } from 'src/environments/environment';

import { Environment } from 'src/environments/environment.model';

import { AppComponent } from './app.component';
import { AppModule } from './app.module';
import { applyPwaBranding, getPwaAppName } from './shared/pwa/pwa-branding';
import { PwaBrandingService } from './shared/pwa/pwa-branding.service';
import { pwaRegistrationOptions } from './shared/pwa/pwa-worker';
import { loadRuntimeEnvironment } from './shared/pwa/runtime-environment';

declare global {
  var CONFIG_BUILD_ID: string | undefined;
}

const loadedConfig = signal(environment);

export const setupRuntimeConfig = (async () => {
  const resolved = await loadRuntimeEnvironment(
    environment,
    window.location.hostname,
    globalThis.CONFIG_BUILD_ID,
  );
  loadedConfig.set(resolved);
  document.title = getPwaAppName(resolved.TENANT, resolved.DEFAULT_LOCALE);
  applyPwaBranding(document, resolved.TENANT, resolved.DEFAULT_LOCALE);
})();

@NgModule({
  imports: [AppModule, BrowserModule],
  bootstrap: [AppComponent],
  providers: [
    provideAgrBrowser(),
    { provide: SwRegistrationOptions, useFactory: pwaRegistrationOptions },
    { provide: PWA_INSTALL_PROMPT_MODE, useValue: 'native' },
    { provide: Title, useExisting: BrandingService },
    {
      provide: ENVIRONMENT_MAPPER,
      useFactory: () => {
        return loadedConfig();
      },
    },
    {
      provide: APP_ID,
      useFactory: () => loadedConfig().APPLICATION_NAME,
    },
  ],
})
export class AppBrowserModule {
  constructor(
    @Inject(DOCUMENT) document: Document,
    @Inject(EnvironmentInjector) env: Environment,
    locale: LocaleService,
    @Inject(BrandingService) branding: PwaBrandingService,
    destroyRef: DestroyRef,
  ) {
    locale.locale$.pipe(takeUntilDestroyed(destroyRef)).subscribe((language) => {
      applyPwaBranding(document, env.TENANT, language);
      branding.setTitle('');
    });
  }
}
